'use client';

import { motion } from 'framer-motion';
import { CTA } from '@/components/CTA/CTA';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';
export function CTASection({
	heading,
	sub_heading,
	features = [
		{ text: '50,000 FREE Credits for SMS, WhatsApp, and RCS' },
		{ text: 'Free Trial Access to enterprise-grade messaging services' },
		{ text: 'Hassle-free API integration and 24x7 support' },
		{ text: 'Trusted by 2,000+ businesses' },
	],
	phoneNumber = '+91-9868629924',
	backgroundColor = 'bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700',
	CTAs = [],
	isFirst = false,
}: components['schemas']['DynamicZoneCtaSectionComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<div className={`relative w-full overflow-hidden ${backgroundColor}`}>
			{/* Background decorative elements */}
			<div className="absolute inset-0 overflow-hidden">
				<div className="absolute -left-1/4 top-0 h-64 w-64 translate-x-1/2 rounded-full bg-white/5 blur-2xl" />
				<div className="absolute -right-1/4 bottom-0 h-64 w-64 translate-x-1/2 rounded-full bg-blue-500/10 blur-2xl" />
			</div>

			<SectionContainer className="relative">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="py-6">
					<SectionHeading className="mb-2 text-center text-3xl font-extrabold text-white md:text-4xl lg:text-5xl">
						{heading}
					</SectionHeading>
					<p className="mb-6 text-center text-lg text-white/90 md:text-xl">
						{sub_heading}
					</p>
					<div className="mx-auto max-w-2xl rounded-xl bg-gradient-to-br from-white/10 to-white/5 p-4 shadow-2xl backdrop-blur-lg md:p-6">
						<ul className="mb-6 grid gap-3 text-sm text-white md:text-base">
							{features.map((feature, index) => (
								<motion.li
									initial={{ opacity: 0, x: -20 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{ delay: index * 0.1 }}
									viewport={{ once: true }}
									key={index}
									className="flex items-center gap-2 rounded-lg bg-white/5 p-2 transition-colors duration-300 hover:bg-white/10">
									<span className="flex-shrink-0 rounded-full bg-green-400/20 p-1">
										<svg
											className="h-3.5 w-3.5 text-green-400 md:h-4 md:w-4"
											fill="currentColor"
											viewBox="0 0 20 20">
											<path
												fillRule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clipRule="evenodd"
											/>
										</svg>
									</span>
									<span className="flex-1">
										{feature.text}
									</span>
								</motion.li>
							))}
						</ul>
						<div className="space-y-6 text-center">
							{phoneNumber && (
								<motion.div
									initial={{ opacity: 0, scale: 0.95 }}
									whileInView={{ opacity: 1, scale: 1 }}
									viewport={{ once: true }}
									className="mb-4 flex items-center justify-center gap-4 text-lg font-semibold text-white md:text-xl">
									<motion.div
										className="flex items-center justify-center"
										animate={{
											rotate: [-5, 5, -5, 5, -5, 0],
											scale: [1, 1.1, 1, 1.1, 1, 1],
										}}
										transition={{
											duration: 1,
											repeat: Infinity,
											repeatDelay: 2,
										}}>
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											className="h-5 w-5 md:h-6 md:w-6">
											<motion.path
												d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
												initial={{ pathLength: 0 }}
												animate={{ pathLength: 1 }}
												transition={{
													duration: 1.5,
													repeat: Infinity,
													repeatDelay: 2,
													ease: 'easeInOut',
												}}
											/>
											<motion.path
												className="text-green-400"
												d="M15 2.5 C15 2.5 16.5 3 19 5.5 C21.5 8 22 9.5 22 9.5"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												initial={{
													pathLength: 0,
													opacity: 0,
												}}
												animate={{
													pathLength: [0, 1, 1, 0],
													opacity: [0, 1, 1, 0],
												}}
												transition={{
													duration: 2,
													repeat: Infinity,
													repeatDelay: 1,
													times: [0, 0.4, 0.6, 1],
												}}
											/>
										</svg>
									</motion.div>
									<span className="inline-flex items-center">
										{phoneNumber}
									</span>
								</motion.div>
							)}
						</div>
						<div className="flex justify-center">
							<CTA CTAs={CTAs} />
						</div>
					</div>
				</motion.div>
			</SectionContainer>
		</div>
	);
}
